/**
 * Boot and platform plumbing: SDK boot, save open, fixed-step loop, input,
 * run lifecycle, and a debug HUD.
 *
 * World restore follows the save contract: a save with a non-zero seed is a
 * returning player, so the world is recreated from that seed and the save's
 * position/edits overlaid — terrain itself is never persisted. Seed 0 is the
 * codec's "never played" default and rolls a fresh seed at boot; unseeded
 * randomness is banned in the sim but allowed here, once, at boot.
 *
 * Runs are ended manually (Enter) in M0: the save is snapshotted and flushed,
 * the score (`stats.maxDepth`) is submitted with seed + input hash + sim
 * version, and the next run starts on the next seed. The client proposes; the
 * server decides. Toks are never awarded here.
 */

import { createTokamio, type SaveHandle, type TokamioSdk } from "@tokamio/sdk";
import { createLoop } from "./loop";
import { createRenderer, type CameraSnapshot } from "./render/renderer";
import {
  SIM_VERSION,
  TICK_MS,
  createWorld,
  hashInputs,
  stepWorld,
  type InputFrame,
  type World,
} from "./sim/world";
import { applySaveToWorld, saveCodec, saveFromWorld, type SaveData } from "./sim/save";

const canvas = document.querySelector<HTMLCanvasElement>("#stage");
if (!canvas) throw new Error("#stage canvas missing");
// Non-null alias: TS does not propagate the module-level narrowing into the
// async boot() body or its closures.
const stage: HTMLCanvasElement = canvas;

const disposers: Array<() => void> = [];
let sdk: TokamioSdk | null = null;
let save: SaveHandle<SaveData> | null = null;

void boot();

async function boot(): Promise<void> {
  // Builds are identical on every deploy, so the environment is resolved at
  // runtime: prod only on the prod hostname, staging everywhere else.
  const env = location.hostname === "tokamio-prod.pages.dev" ? "prod" : "dev";
  sdk = await createTokamio({
    gameId: "blockwild",
    gameVersion: __APP_VERSION__,
    simVersion: SIM_VERSION,
    env,
  });
  save = await sdk.saves.open(saveCodec);

  const renderer = createRenderer(stage);
  disposers.push(() => renderer.dispose());

  let world: World;
  if (save.data.seed !== 0) {
    world = createWorld(save.data.seed);
    applySaveToWorld(world, save.data);
  } else {
    world = createWorld((Date.now() ^ (Math.random() * 0xffffffff)) >>> 0);
  }
  let previous: CameraSnapshot | null = null;
  let startedAt = Date.now();

  // The input stream is recorded, not just read. The hash of it is what makes
  // a submitted score verifiable server-side.
  const inputs: InputFrame[] = [];

  /* ---------------------------------------------------------------- input */

  const PITCH_LIMIT = 1.55;
  const LOOK_SPEED = 0.0025;
  /** Touchstick offset in pixels that reads as full intent. */
  const STICK_RADIUS = 48;

  const keys = new Set<string>();
  let lookYaw = world.yaw;
  let lookPitch = world.pitch;
  let pointerLocked = false;
  let dragging = false;
  const touchMove = { x: 0, z: 0 };
  let moveStick: { id: number; startX: number; startY: number } | null = null;
  let lookTouch: { id: number; lastX: number; lastY: number } | null = null;

  const clamp = (value: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, value));

  const composeInput = (): InputFrame => {
    let moveX = touchMove.x;
    let moveY = 0;
    let moveZ = touchMove.z;
    if (keys.has("KeyW") || keys.has("ArrowUp")) moveZ += 1;
    if (keys.has("KeyS") || keys.has("ArrowDown")) moveZ -= 1;
    if (keys.has("KeyD") || keys.has("ArrowRight")) moveX += 1;
    if (keys.has("KeyA") || keys.has("ArrowLeft")) moveX -= 1;
    if (keys.has("Space")) moveY += 1;
    if (keys.has("ShiftLeft")) moveY -= 1;
    return {
      moveX: clamp(moveX, -1, 1),
      moveY: clamp(moveY, -1, 1),
      moveZ: clamp(moveZ, -1, 1),
      yaw: lookYaw,
      pitch: lookPitch,
    };
  };

  const HANDLED_KEYS = new Set([
    "KeyW",
    "KeyA",
    "KeyS",
    "KeyD",
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "Space",
    "ShiftLeft",
  ]);

  const onKey = (event: KeyboardEvent): void => {
    if (event.type === "keydown") {
      if (event.code === "Enter") {
        void endRun();
        return;
      }
      keys.add(event.code);
    } else {
      keys.delete(event.code);
    }
    if (HANDLED_KEYS.has(event.code)) event.preventDefault();
  };

  const applyLook = (dx: number, dy: number): void => {
    lookYaw -= dx * LOOK_SPEED;
    lookPitch = clamp(lookPitch - dy * LOOK_SPEED, -PITCH_LIMIT, PITCH_LIMIT);
  };

  const onMouseMove = (event: MouseEvent): void => {
    if (pointerLocked || dragging) applyLook(event.movementX, event.movementY);
  };
  const onMouseDown = (event: MouseEvent): void => {
    if (event.button === 0) dragging = true;
  };
  const onMouseUp = (): void => {
    dragging = false;
  };
  const onClick = (): void => {
    // Pointer lock is the primary look mode; drag-look above is the fallback
    // for browsers that deny or lack it (request can fail async or sync).
    if (pointerLocked) return;
    try {
      const request = stage.requestPointerLock() as unknown;
      if (request instanceof Promise) request.catch(() => undefined);
    } catch {
      // Pointer lock unsupported — drag-look still works.
    }
  };
  const onPointerLockChange = (): void => {
    pointerLocked = document.pointerLockElement === stage;
  };
  const onPointerLockError = (): void => {
    pointerLocked = false;
  };

  // Twin-stick touch: left half of the screen is a virtual move stick, right
  // half is look drag. Each side tracks one touch by identifier.
  const onTouchStart = (event: TouchEvent): void => {
    for (const touch of Array.from(event.changedTouches)) {
      if (touch.clientX < window.innerWidth / 2 && moveStick === null) {
        moveStick = { id: touch.identifier, startX: touch.clientX, startY: touch.clientY };
      } else if (lookTouch === null) {
        lookTouch = { id: touch.identifier, lastX: touch.clientX, lastY: touch.clientY };
      }
    }
    event.preventDefault();
  };
  const onTouchMove = (event: TouchEvent): void => {
    for (const touch of Array.from(event.changedTouches)) {
      if (moveStick !== null && touch.identifier === moveStick.id) {
        touchMove.x = clamp((touch.clientX - moveStick.startX) / STICK_RADIUS, -1, 1);
        touchMove.z = clamp(-(touch.clientY - moveStick.startY) / STICK_RADIUS, -1, 1);
      } else if (lookTouch !== null && touch.identifier === lookTouch.id) {
        applyLook((touch.clientX - lookTouch.lastX) * 2, (touch.clientY - lookTouch.lastY) * 2);
        lookTouch = { id: lookTouch.id, lastX: touch.clientX, lastY: touch.clientY };
      }
    }
    event.preventDefault();
  };
  const onTouchEnd = (event: TouchEvent): void => {
    for (const touch of Array.from(event.changedTouches)) {
      if (moveStick !== null && touch.identifier === moveStick.id) {
        moveStick = null;
        touchMove.x = 0;
        touchMove.z = 0;
      }
      if (lookTouch !== null && touch.identifier === lookTouch.id) lookTouch = null;
    }
  };

  /* ------------------------------------------------------------------ HUD */

  const hud = document.createElement("div");
  hud.style.cssText = [
    "position:fixed",
    "left:8px",
    "top:8px",
    "padding:4px 8px",
    "font:12px/1.5 ui-monospace,monospace",
    "color:#fff",
    "background:rgba(0,0,0,0.45)",
    "border-radius:4px",
    "pointer-events:none",
    "white-space:pre",
    "z-index:10",
  ].join(";");
  document.body.appendChild(hud);
  disposers.push(() => hud.remove());

  let hudNote = "";
  let hudNoteTimer = 0;
  const setHudNote = (text: string): void => {
    hudNote = text;
    window.clearTimeout(hudNoteTimer);
    hudNoteTimer = window.setTimeout(() => {
      hudNote = "";
    }, 5000);
  };
  disposers.push(() => window.clearTimeout(hudNoteTimer));

  // Once per render, not per tick: textContent writes are not free.
  const updateHud = (): void => {
    const depth = Math.max(0, Math.floor(-world.position.y));
    hud.textContent = `depth ${depth} · seed ${world.seed} · Enter: end run${hudNote !== "" ? `\n${hudNote}` : ""}`;
  };

  /* ------------------------------------------------------------- run flow */

  let ending = false;

  async function endRun(): Promise<void> {
    if (ending) return;
    ending = true;
    try {
      loop.stop();
      const durationMs = Date.now() - startedAt;

      save?.mutate((d) => {
        Object.assign(d, saveFromWorld(world, d));
        d.runsPlayed += 1;
      });
      await save?.flush().catch(() => undefined);

      sdk?.events.track("run_end", { durationMs, score: world.stats.maxDepth, outcome: "quit" });

      // The client proposes a score. The server decides. Never award Toks here.
      let note = "score not submitted";
      try {
        const result = await sdk?.scores.submitRun({
          seed: world.seed,
          startedAt,
          durationMs,
          score: world.stats.maxDepth,
          stats: { ...world.stats },
          inputHash: hashInputs(inputs),
          simVersion: SIM_VERSION,
        });
        if (result) {
          note = result.accepted
            ? `score accepted · rank ${result.rank ?? "—"} · +${result.toksAwarded} toks`
            : `score rejected (${result.reason ?? "unknown"})`;
          if (!result.accepted) {
            sdk?.events.track("error", { message: `run rejected: ${result.reason ?? "unknown"}`, fatal: false });
          }
        }
      } catch (error) {
        note = "score submit failed";
        sdk?.events.track("error", {
          message: `run submit failed: ${error instanceof Error ? error.message : String(error)}`,
          fatal: false,
        });
      }
      setHudNote(note);

      // Next run on the next seed: deterministic, no randomness needed.
      const nextSeed = (world.seed + 1) >>> 0;
      world = createWorld(nextSeed);
      previous = null;
      inputs.length = 0;
      startedAt = Date.now();
      lookYaw = world.yaw;
      lookPitch = world.pitch;
      sdk?.events.track("run_start", { seed: nextSeed });
      loop.start();
    } finally {
      ending = false;
    }
  }

  /* ----------------------------------------------------------------- loop */

  const loop = createLoop(
    {
      step() {
        previous = {
          x: world.position.x,
          y: world.position.y,
          z: world.position.z,
          yaw: world.yaw,
          pitch: world.pitch,
        };
        const input = composeInput();
        inputs.push(input);
        stepWorld(world, input);
      },
      render(alpha) {
        renderer.render(world, previous, alpha);
        updateHud();
      },
    },
    { stepMs: TICK_MS },
  );

  sdk.events.track("run_start", { seed: world.seed });
  loop.start();

  // The SDK maps tab-hide and window-blur to pause("blur") and refcounts
  // pause reasons internally, so the loop just follows the edges.
  disposers.push(sdk.lifecycle.onPause(() => loop.stop()));
  disposers.push(sdk.lifecycle.onResume(() => loop.start()));
  disposers.push(
    sdk.lifecycle.onBeforeUnload(() => {
      // Synchronous only. Async work will not finish during unload.
      save?.mutate((d) => Object.assign(d, saveFromWorld(world, d)));
    }),
  );

  window.addEventListener("keydown", onKey);
  window.addEventListener("keyup", onKey);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mousedown", onMouseDown);
  window.addEventListener("mouseup", onMouseUp);
  stage.addEventListener("click", onClick);
  document.addEventListener("pointerlockchange", onPointerLockChange);
  document.addEventListener("pointerlockerror", onPointerLockError);
  stage.addEventListener("touchstart", onTouchStart, { passive: false });
  stage.addEventListener("touchmove", onTouchMove, { passive: false });
  stage.addEventListener("touchend", onTouchEnd);
  stage.addEventListener("touchcancel", onTouchEnd);
  disposers.push(() => {
    window.removeEventListener("keydown", onKey);
    window.removeEventListener("keyup", onKey);
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mousedown", onMouseDown);
    window.removeEventListener("mouseup", onMouseUp);
    stage.removeEventListener("click", onClick);
    document.removeEventListener("pointerlockchange", onPointerLockChange);
    document.removeEventListener("pointerlockerror", onPointerLockError);
    stage.removeEventListener("touchstart", onTouchStart);
    stage.removeEventListener("touchmove", onTouchMove);
    stage.removeEventListener("touchend", onTouchEnd);
    stage.removeEventListener("touchcancel", onTouchEnd);
  });
}

/** Called by the shell when the player leaves the game. Must release everything. */
export function teardown(): void {
  while (disposers.length) disposers.pop()?.();
  sdk?.destroy();
  sdk = null;
  save = null;
}

declare const __APP_VERSION__: string;
